import { useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { setAgentFilterSearchParam } from "@/pages/agent/utils/agent-search-params.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import {
  AGENT_TASK_PROJECT_ID_SEARCH_PARAM,
  AGENT_TASK_SEARCH_QUERY_SEARCH_PARAM,
  AGENT_TASK_STATUS_SEARCH_PARAM,
  AGENT_TASK_VIEW_SEARCH_PARAM,
} from "@/store/agent-overlay/types";

export const ALL_PROJECTS_FILTER = "all-projects";
export const ALL_STATUSES_FILTER = "all-statuses";

export const TaskView = {
  TopOfMind: "top-of-mind",
  RecentlyAdded: "recently-added",
  RecentlyUpdated: "recently-updated",
  DueSoon: "due-soon",
  RecentlyOverdue: "recently-overdue",
  AllTasks: "all-tasks",
} as const;

export type TaskView = (typeof TaskView)[keyof typeof TaskView];

export const DEFAULT_TASK_VIEW: TaskView = TaskView.TopOfMind;

const TASK_VIEWS = new Set<string>(Object.values(TaskView));

const isTaskView = (value: string): value is TaskView => TASK_VIEWS.has(value);

export const useAgentTasksSearchParams = ({
  defaultView = DEFAULT_TASK_VIEW,
}: { defaultView?: TaskView } = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  // Replacing an entry drops its state unless it is passed back, which would
  // discard the overlay's history marker and break closing via back-navigation.
  const { state: locationState } = useLocation();

  const projectFilter = searchParams.get(AGENT_TASK_PROJECT_ID_SEARCH_PARAM) ?? ALL_PROJECTS_FILTER;
  const statusFilter = normalizeStatusValue({
    status: searchParams.get(AGENT_TASK_STATUS_SEARCH_PARAM) ?? ALL_STATUSES_FILTER,
  });
  const searchQuery = searchParams.get(AGENT_TASK_SEARCH_QUERY_SEARCH_PARAM) ?? "";
  const rawViewFilter = searchParams.get(AGENT_TASK_VIEW_SEARCH_PARAM) ?? defaultView;
  const viewFilter: TaskView = isTaskView(rawViewFilter) ? rawViewFilter : defaultView;

  const updateTaskFilters = useCallback(
    ({
      nextProjectFilter,
      nextStatusFilter,
      nextSearchQuery,
      nextViewFilter,
    }: {
      nextProjectFilter?: string;
      nextStatusFilter?: string;
      nextSearchQuery?: string;
      nextViewFilter?: string;
    }) => {
      setSearchParams(
        previousSearchParams => {
          const nextSearchParams = new URLSearchParams(previousSearchParams);

          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_TASK_PROJECT_ID_SEARCH_PARAM,
            value: nextProjectFilter,
            defaultValue: ALL_PROJECTS_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_TASK_STATUS_SEARCH_PARAM,
            value: nextStatusFilter,
            defaultValue: ALL_STATUSES_FILTER,
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_TASK_SEARCH_QUERY_SEARCH_PARAM,
            value: nextSearchQuery,
            defaultValue: "",
          });
          setAgentFilterSearchParam({
            searchParams: nextSearchParams,
            name: AGENT_TASK_VIEW_SEARCH_PARAM,
            value: nextViewFilter,
            defaultValue: defaultView,
          });

          return nextSearchParams;
        },
        { replace: true, state: locationState }
      );
    },
    [defaultView, locationState, setSearchParams]
  );

  return {
    projectFilter,
    statusFilter,
    searchQuery,
    viewFilter,
    updateTaskFilters,
  };
};
